import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';
import ConfirmDialog from '../../../components/ConfirmDialog.jsx';

function formatPrice(cents) {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

export default function SuperadminDealsShow({ deal }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(deal.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  // Holds the pending action until the dialog confirms it. null = closed.
  const [pendingAction, setPendingAction] = useState(null);

  const runPendingAction = () => {
    if (!pendingAction) return;
    setBusy(true);
    router.post(pendingAction.path, {}, {
      onFinish: () => { setBusy(false); setPendingAction(null); },
    });
  };

  const savings = (deal.list_subtotal_cents || 0) - (deal.price_cents || 0);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <Link href="/superadmin/deals" className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
            ← All deals
          </Link>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900">{deal.name}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {deal.plan === 'subscription' ? 'Subscription' : 'One-time'} ·{' '}
            Group / facility · {deal.facility_type_label}
            {deal.author_name && <> · created by {deal.author_name}</>}
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Share link</h2>
          {deal.redeemable ? (
            <>
              <div className="flex gap-2">
                <input readOnly value={deal.share_url}
                       className="flex-1 min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-mono text-gray-700" />
                <button type="button" onClick={copyLink}
                        className="shrink-0 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 cursor-pointer transition">
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Single use — the link stops working the moment someone completes checkout.
              </p>
            </>
          ) : (
            <p className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-600">
              This link is no longer active
              {deal.status === 'redeemed' && ' — it has been redeemed.'}
              {deal.status === 'voided' && ' — it was withdrawn.'}
              {deal.status === 'active' && deal.expired && ' — it expired.'}
            </p>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">What's included</h2>
          <div className="divide-y divide-gray-100">
            {(deal.items || []).map((item) => (
              <div key={item.name} className="py-2 flex justify-between gap-3 text-sm">
                <span className="text-gray-900">{item.name}</span>
                <span className="text-gray-500">×{item.quantity}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200 space-y-1.5 text-sm">
            {deal.list_subtotal_cents != null && (
              <div className="flex justify-between text-gray-600">
                <span>Standard price</span><span>{formatPrice(deal.list_subtotal_cents)}</span>
              </div>
            )}
            {savings > 0 && (
              <div className="flex justify-between font-medium text-green-700">
                <span>Discount</span><span>−{formatPrice(savings)}</span>
              </div>
            )}
            <div className="pt-1 flex justify-between items-center">
              <span className="font-semibold text-gray-900">
                {deal.plan === 'subscription' ? 'Customer pays first term' : 'Customer pays'}
              </span>
              <span className="text-xl font-bold text-indigo-600">{formatPrice(deal.price_cents)}</span>
            </div>
          </div>
          {deal.notes && <p className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600">{deal.notes}</p>}
          <p className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500">
            Deals can't be edited — this link may already be with the customer, so the terms are fixed.
            To change the offer, withdraw it and{' '}
            <Link href="/superadmin/deals/new" className="font-semibold text-indigo-600 hover:text-indigo-700">
              create a new one
            </Link>.
          </p>
        </div>

        {deal.redemption && deal.redemption.kind === 'facility' && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-2">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Redemption</h2>
            <p className="text-sm text-gray-700">{deal.redemption.facility_name || '—'}</p>
            <p className="text-sm text-gray-600">
              {deal.redemption.seats_credited
                ? 'Paid — seats credited to the facility'
                : 'Started checkout but the seats have not been credited yet'}
              {deal.redemption.redeemed_at ? ` · claimed ${deal.redemption.redeemed_at}` : ''}
            </p>
            {deal.redemption.stripe_object_id && (
              <p className="text-xs font-mono text-gray-400 break-all">{deal.redemption.stripe_object_id}</p>
            )}
          </div>
        )}

        {deal.redemption && deal.redemption.kind !== 'facility' && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-2">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Redemption</h2>
            <p className="text-sm text-gray-700">{deal.redemption.email || '—'}</p>
            <p className="text-sm text-gray-600">
              {deal.redemption.paid ? `Paid ${deal.redemption.paid_at}` : 'Started checkout but has not paid'}
              {' · '}account {deal.redemption.status}
            </p>
            {(deal.redemption.stripe_payment_intent_id || deal.redemption.stripe_subscription_id) && (
              <p className="text-xs font-mono text-gray-400 break-all">
                {deal.redemption.stripe_payment_intent_id || deal.redemption.stripe_subscription_id}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          {deal.status === 'active' && (
            <button type="button" disabled={busy}
                    onClick={() => setPendingAction({
                      path: `/superadmin/deals/${deal.id}/void`,
                      title: 'Withdraw this deal?',
                      body: 'The share link stops working immediately. You can reopen it later if you change your mind.',
                      confirmLabel: 'Withdraw deal',
                      tone: 'danger',
                    })}
                    className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-red-700 bg-white hover:bg-red-50 border border-red-200 transition cursor-pointer disabled:opacity-50">
              Withdraw
            </button>
          )}
          {deal.status === 'redeemed' && deal.redemption &&
           !(deal.redemption.kind === 'facility' ? deal.redemption.seats_credited : deal.redemption.paid) && (
            <button type="button" disabled={busy}
                    onClick={() => setPendingAction({
                      path: `/superadmin/deals/${deal.id}/reopen`,
                      title: 'Reopen this deal?',
                      body: 'The share link will work again and the offer can be redeemed by whoever holds it.',
                      confirmLabel: 'Reopen deal',
                      tone: 'primary',
                    })}
                    className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 transition cursor-pointer disabled:opacity-50">
              Reopen (checkout abandoned)
            </button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={pendingAction !== null}
        title={pendingAction?.title}
        body={pendingAction?.body}
        confirmLabel={pendingAction?.confirmLabel}
        tone={pendingAction?.tone}
        busy={busy}
        onConfirm={runPendingAction}
        onCancel={() => setPendingAction(null)}
      />
    </Layout>
  );
}
